import { useAdviserSubscriptions } from 'hooks/data/adviser/useAdviserSubscriptions';
import { Api } from 'further-types/adviser';
import { Button, CircularProgress } from '@mui/material';
import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useFilters, usePagination } from 'further-ui/hooks';
import SearchField from 'components/SearchField';
import {
  useDownloadByAdviserId,
  useDownloadByInvestmentId,
} from 'hooks/data/eisWizard/useDownloadEisCertificatePdfs';
import { useGetRole } from 'hooks/ui/useGetRole';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';
import { SortOrder } from 'further-types/utils';
import AdviserInvestmentsTable from './AdviserInvestmentsTable';
import { AdviserSnapshot } from './AdviserSnapshot';
import GridContainer from 'components/GridContainer';
import Heading from 'components/Heading';

type Props = {
  adviserId: string;
  adviserName?: string;
};

type Filters = {
  search: string;
};

type DownloadCertificatesButtonProps = {
  adviserId: string;
  adviserName?: string;
  disabled?: boolean;
};

const useStyles = makeStyles()((theme) => ({
  flex: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '1.5rem',
  },
  divider: {
    margin: '1.5rem 0',
  },
  title: {
    margin: 0,
  },
  allEisButton: {
    marginLeft: 'auto',
    marginRight: '0.5rem',
  },
  customButton: {
    minWidth: '35px',
    color: theme.palette.text.secondary,
    '&:hover': {
      color: theme.palette.text.primary,
    },
  },
  dFlex: {
    display: 'flex',
  },
  controlsContainer: {
    display: 'flex',
    marginLeft: 'auto',
  },
}));

const DownloadCertificatesButton = ({
  adviserId,
  adviserName,
  disabled,
}: DownloadCertificatesButtonProps) => {
  const { classes } = useStyles();
  const downloadByAdviserId = useDownloadByAdviserId();

  return (
    <Button
      size="small"
      variant="contained"
      color="primary"
      className={classes.allEisButton}
      onClick={() => downloadByAdviserId.download({ adviserId, adviserName })}
      disabled={disabled || downloadByAdviserId.isLoading}
    >
      {downloadByAdviserId.isLoading ? (
        <CircularProgress
          style={{ color: 'rgba(0, 0, 0, 0.26)', marginRight: 10 }}
          size={18}
        />
      ) : null}
      Download all EIS certificates
    </Button>
  );
};

const AdviserInvestments = ({ adviserId, adviserName }: Props) => {
  const { classes } = useStyles();
  const { isSuperAdmin } = useGetRole();
  const [canDownloadSeis] = useHasPermissions([RolePermission.DownloadSeis]);
  const downloadByInvestmentId = useDownloadByInvestmentId();

  const handleInvestmentCertificatesDownload = (
    investment: Api.AdviserSubscription,
  ) => {
    downloadByInvestmentId.download({
      investmentId: investment._id,
      fullName: investment.investorId.fullName,
      trancheName: `${investment.firmId.firmName}-${investment.fundId.fundName}`,
    });
  };

  const pagination = usePagination({
    id: `adviserInvestments-${adviserId}`,
    order: SortOrder.Desc,
    orderBy: 'investmentDate',
  });

  const { filters, handleChangeFilters } = useFilters<Filters>(
    `adviserInvestments-${adviserId}`,
    {
      onFiltersChange: () => pagination.toFirstPage(),
    },
  );
  const { data, isFetching } = useAdviserSubscriptions({
    adviserId,
    params: {
      page: pagination.page,
      pageSize: pagination.rowsPerPage,
      search: filters.search,
      order: pagination.order,
      orderBy: pagination.orderBy,
    },
  });

  return (
    <GridContainer>
      <Grid size={{ xs: 12 }}>
        <Heading variant="h2">Adviser snapshot</Heading>
        <AdviserSnapshot isLoading={isFetching} totals={data?.totals} />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Grid container alignItems="center" className={classes.divider}>
          <Heading variant="h2" noMargin>
            Linked subscriptions
          </Heading>

          <div className={classes.controlsContainer}>
            {canDownloadSeis &&
            data?.subscriptions.length &&
            data?.totals.investments < 500 ? (
              <DownloadCertificatesButton
                adviserId={adviserId}
                adviserName={adviserName}
              />
            ) : null}

            <SearchField
              onSearch={(search) => handleChangeFilters({ search })}
              variant="outlined"
              name="investorName"
              id="investorName"
              placeholder="Search by investor name"
              allowEmptyQuery
              autoSearchOnDebounce
              fullWidth
              width="20rem"
            />
          </div>
        </Grid>
        <AdviserInvestmentsTable
          subscriptions={data?.subscriptions ?? []}
          totalInvestments={data?.totals?.investments ?? 0}
          pagination={pagination}
          loading={isFetching}
          onDownloadCertificates={handleInvestmentCertificatesDownload}
          isSuperAdmin={isSuperAdmin}
          canDownloadSeis={canDownloadSeis}
        />
      </Grid>
    </GridContainer>
  );
};

export default AdviserInvestments;
